import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import dayjs from "dayjs";

import { serverResponseFormat } from "@similarweb/pro-logic-components";

import { SidePanelDatePicker } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/SidePanelDatePicker";
import type { IUrlSidePanelItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/types";

export interface IUseSourcesSidePanelDatePickerProps {
    data: IUrlSidePanelItem[];
}

interface IUseSourcesSidePanelDatePickerResult {
    currentDateData: IUrlSidePanelItem[];
    datePicker: ReactNode;
}

export const useSourcesSidePanelDatePicker = ({
    data,
}: IUseSourcesSidePanelDatePickerProps): IUseSourcesSidePanelDatePickerResult => {
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const dataByDate = useMemo(() => {
        if (!data?.length) return {};
        return Object.groupBy(data, (item) => item.date ?? "");
    }, [data]);

    const allDateStrings = useMemo(() => Object.keys(dataByDate).filter(Boolean).sort(), [dataByDate]);

    const supportedDates = useMemo(() => allDateStrings.map((dateStr) => dayjs.utc(dateStr)), [allDateStrings]);

    const activeDate = allDateStrings.includes(selectedDate!) ? selectedDate! : (allDateStrings.at(-1) ?? null);

    return {
        currentDateData: activeDate ? (dataByDate[activeDate] ?? []) : [],
        datePicker: (
            <SidePanelDatePicker
                date={activeDate ? dayjs.utc(activeDate) : dayjs.utc()}
                onDateChange={(d) => setSelectedDate(d.format(serverResponseFormat))}
                supportedDates={supportedDates}
                isDisabled={allDateStrings.length === 0}
            />
        ),
    };
};
